/**
 * Phone number validation utility functions
 * Validates phone numbers based on country codes
 */

interface ValidationResult {
  isValid: boolean;
  message?: string;
}

/**
 * Validates a phone number based on its country code
 * Currently supports validation for:
 * - +1 (US/Canada): Must be 10 digits
 * - +52 (Mexico): Must be 10 digits
 * - +92 (Pakistan): Must be 10 digits
 * 
 * @param phoneNumber - The full phone number including country code (e.g., "+1-1234567890")
 * @param expectedCountryCode - Optional parameter to enforce specific country validation
 * @returns ValidationResult object with isValid flag and optional error message
 */
export function validatePhoneByAreaCode(phoneNumber: string, expectedCountryCode?: string): ValidationResult {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return { isValid: false, message: 'Phone number is required' };
  }

  // Extract the country code and the actual number
  const parts = phoneNumber.split(/[-\s]/);
  if (parts.length < 2) {
    return { isValid: false, message: 'Invalid phone number format' };
  }

  const countryCode = parts[0];
  const number = parts.slice(1).join('').replace(/\D/g, ''); // Remove any non-digit characters

  // If expectedCountryCode is provided, check if it matches
  if (expectedCountryCode && countryCode !== expectedCountryCode) {
    const countryName = expectedCountryCode === '+1' ? 'US/Canada' : 
                       expectedCountryCode === '+52' ? 'Mexico' :
                       expectedCountryCode === '+92' ? 'Pakistan' : 'selected country';
    return { 
      isValid: false, 
      message: `Please enter a ${countryName} phone number` 
    };
  }

  // Validate based on country code
  switch (countryCode) {
    case '+1': // US/Canada
      if (number.length !== 10) {
        return { 
          isValid: false, 
          message: 'US/Canada phone numbers must be 10 digits' 
        };
      }
      // Check if the number follows North American Numbering Plan
      const areaCode = number.substring(0, 3);
      if (['000', '555', '911', '999'].includes(areaCode) || 
          areaCode.startsWith('0') || 
          areaCode.startsWith('1')) {
        return { 
          isValid: false, 
          message: 'Enter a valid phone number' 
        };
      }
      break;

    case '+52': // Mexico
      if (number.length !== 10) {
        return { 
          isValid: false, 
          message: 'Mexico phone numbers must be 10 digits' 
        };
      }
      // Check if the number follows Mexico mobile number format
      // Mexico mobile numbers typically start with 1, 2, 3, 4, 5, 6, 7, 8, or 9
      const firstDigit = number.substring(0, 1);
      if (!['1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(firstDigit)) {
        return { 
          isValid: false, 
          message: 'Invalid Mexico phone number format' 
        };
      }
      break;

    case '+92': // Pakistan
      if (number.length !== 10) {
        return { 
          isValid: false, 
          message: 'Pakistan phone numbers must be 10 digits' 
        };
      }
      // Check if the number follows Pakistan mobile number format
      // Pakistan mobile numbers can start with 30, 31, 32, 33, 34, 35, 36, 37 after country code
      const firstTwoDigits = number.substring(0, 2);
      const validPrefixes = ['30', '31', '32', '33', '34', '35', '36', '37'];
      if (!validPrefixes.includes(firstTwoDigits)) {
        return { 
          isValid: false, 
          message: 'Invalid Pakistan mobile number format' 
        };
      }
      break;

    default:
      // For other country codes, just ensure minimum length
      if (number.length < 8) {
        return { 
          isValid: false, 
          message: 'Phone number must have at least 8 digits' 
        };
      }
  }

  return { isValid: true };
}

/**
 * Checks if a phone number has a valid format
 * This is a simpler validation that just checks for basic format
 * 
 * @param phoneNumber - The full phone number including country code
 * @returns boolean indicating if the format is valid
 */
export function hasValidPhoneFormat(phoneNumber: string): boolean {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return false;
  }

  // Check if it starts with a plus sign followed by digits
  const hasValidCountryCode = /^\+\d+/.test(phoneNumber);
  
  // Check if it has a separator and at least some digits after
  const hasValidFormat = /^\+\d+[-\s]\d+/.test(phoneNumber);

  return hasValidCountryCode && hasValidFormat;
}